import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MomentModule } from 'ngx-moment';
import { from, of } from 'rxjs';
import { catchError, concatMap, finalize, tap } from 'rxjs/operators';
import { DocketService } from '../../../shared/services/docket.service';
import moment from 'moment';
import { startOfDay } from 'date-fns';

@Component({
  selector: 'app-complete-batch-rollover-dialog',
  templateUrl: './complete-batch-rollover-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MomentModule
  ],
  providers: [DocketService]
})
export class CompleteBatchRolloverDialogComponent implements OnInit {
  rolloverForm: FormGroup;
  processing = false;
  completed = false;
  results: RolloverResult[] = [];
  currentProcessingIndex = -1;
  maxDate = new Date();
  finalResultSuccess = 0;
  finalResultError = 0;

  constructor(
    public dialogRef: MatDialogRef<CompleteBatchRolloverDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { docketIds: number[] },
    private fb: FormBuilder,
    private docketService: DocketService
  ) {
    this.rolloverForm = this.fb.group({
      rolloverDate: [
        moment(new Date()),
        [Validators.required, this.pastDateValidator]
      ]
    });
  }

  ngOnInit(): void {}

  pastDateValidator(control: any) {
    const selectedDate = moment(control.value);
    const now = moment();

    if (selectedDate > now) {
      return { futureDate: true };
    }

    return null;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSubmit(): void {
    if (this.rolloverForm.invalid) {
      return;
    }

    this.processing = true;
    this.results = [];
    this.currentProcessingIndex = 0;

    const rolloverDate = this.getRolloverDate();

    // Create an array of observables for each docket ID
    from(this.data.docketIds)
      .pipe(
        // Process each docket sequentially
        concatMap((docketId) => {
          // Create a result object to track this docket's processing
          const result: RolloverResult = {
            docketId,
            status: 'processing',
            message: 'Processing...'
          };

          this.results.push(result);

          // Return an observable that updates the result object
          return this.docketService
            .update(docketId, { rollover_date: rolloverDate.toISOString() })
            .pipe(
              // On success, update the result
              tap(() => {
                result.status = 'success';
                result.message = 'Successfully rolled over';
              }),
              // On error, update the result but don't stop the sequence
              catchError((error) => {
                console.log(error);
                result.status = 'error';
                result.message = error.error?.message || 'Failed to rollover';
                // Return an empty observable to continue the sequence
                return of(null);
              }),
              // Always increment the processing index
              finalize(() => {
                this.currentProcessingIndex++;
              })
            );
        }),
        // When all dockets are processed, mark as completed
        finalize(() => {
          this.processing = false;
          this.completed = true;
          this.finalResultSuccess = this.results.filter(
            (result) => result.status === 'success'
          ).length;
          this.finalResultError = this.results.filter(
            (result) => result.status === 'error'
          ).length;
        })
      )
      .subscribe();
  }

  getProgressPercentage(): number {
    if (this.data.docketIds.length === 0) return 0;
    return (this.currentProcessingIndex / this.data.docketIds.length) * 100;
  }

  close(completed = false): void {
    this.dialogRef.close(completed);
  }

  getRolloverDate(): Date {
    const supplied = this.rolloverForm.get('rolloverDate')
      ?.value as moment.Moment;

    if (supplied.toDate().valueOf() === startOfDay(new Date()).valueOf()) {
      return new Date();
    }

    return supplied.toDate();
  }
}

interface RolloverResult {
  docketId: number;
  status: 'processing' | 'success' | 'error';
  message: string;
}
